import type { SbRichTextProps } from '@storyblok/richtext';
import { attrsToHtmlString, processAttrs } from '@storyblok/richtext';

/** Hrefs a browser can resolve on its own: scheme, protocol-relative, root-relative, fragment or query. */
const RESOLVABLE_HREF = /^([a-z][a-z\d+.-]*:|\/\/|\/|#|\?)/i;

/** Anchors an internal link to the site root, so it can never be appended to the current path. */
function toRootRelative(href: string): string {
	return href === '' || RESOLVABLE_HREF.test(href) ? href : `/${href}`;
}

/**
 * Link renderer for Storyblok richtext. Storyblok can hand us an internal link without a leading
 * slash, which browsers then resolve against the current directory and turn into a duplicated path.
 * Optionally pass prependLink to point internal links at another host.
 */
export function createInternalLinkRenderer(prependLink = '') {
	return ({ attrs, children }: SbRichTextProps<'link'>): string => {
		// processAttrs builds the href (anchor, mailto:) and drops Storyblok-only attributes.
		const htmlAttrs = processAttrs('link', attrs ?? {});
		const href = htmlAttrs['href'];

		const resolvedAttrs =
			attrs?.linktype === 'story' && typeof href === 'string'
				? { ...htmlAttrs, href: prependLink + toRootRelative(href) }
				: htmlAttrs;

		return `<a${attrsToHtmlString(resolvedAttrs)}>${children}</a>`;
	};
}
